import { useMemo } from 'react'
import { useCurrentFrame, useVideoConfig } from 'remotion'
import {
  activeSpeakerAt,
  pinnedSpeaker,
  speakerAtTime,
  type CurrentSpeaker,
  type ParticipantLike,
  type SpeakerSpanLike,
} from './captionWords'
import { useAssets, useFollowedScenes } from './contexts'
import { framesToSeconds } from './frames'
import { captionSourceOf } from './sceneWindow'

/** How far a pinned slot dims while its participant is not the active speaker (#566). */
export const INACTIVE_SPEAKER_OPACITY = 0.45

export type SpeakerSlot = {
  /** Who the element shows; `null` for nobody. */
  speaker: CurrentSpeaker | null
  /** Whether to draw the element lit: always for an unpinned one. */
  active: boolean
}

const NOBODY: SpeakerSlot = { speaker: null, active: true }

/** Whether any followed item is on screen at the absolute frame `at`. */
export function playsAt(
  followed: readonly { from: number; durationInFrames: number }[],
  at: number,
): boolean {
  return followed.some(
    (item) => at >= item.from && at < item.from + item.durationInFrames,
  )
}

/**
 * Who a speaker element shows in its scene at this frame (#77, #541, #566).
 * Unpinned, it is whoever is speaking: the scene maps the time back into its
 * recording and the transcript's speaker spans name them (`speakerAtTime`:
 * the last speaker holds through a pause). Pinned to a participant, it is
 * always that person (`pinnedSpeaker`), and `active` says whether they are
 * the one talking now (`activeSpeakerAt`), which the layers draw as a lit
 * ring and full opacity against a dimmed slot. `speaker` is `null` with no
 * binding, outside every item it follows (pinned or not), or nobody to show. Shared by dynamic texts
 * (name, handle) and the speaker picture (avatar). `legacyTrackId` is the
 * previous generation's binding (`useFollowedScenes`).
 */
export function useCurrentSpeaker(
  sceneId: string | undefined,
  legacyTrackId: string | undefined,
  itemFrom: number,
  pin?: string,
): SpeakerSlot {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const assets = useAssets()
  const mediaItems = useFollowedScenes(sceneId, legacyTrackId)
  return useMemo(() => {
    if (sceneId === undefined && legacyTrackId === undefined) return NOBODY
    // Outside what it follows a speaker element shows nobody, pinned or not
    // (#541, eng D15): it keeps its own span, and a trim of its parent never
    // clamps it, so a pinned label must not hang over the next scene.
    if (!playsAt(mediaItems, itemFrom + frame)) return NOBODY
    const speakers: Record<string, SpeakerSpanLike[]> = {}
    const participants: Record<string, ParticipantLike[]> = {}
    const words: Record<string, { start: number }[]> = {}
    for (const asset of Object.values(assets)) {
      if (asset.type !== 'transcript') continue
      if (asset.speakers) speakers[asset.recordingId] = asset.speakers
      words[asset.recordingId] = asset.words
      if (asset.participants)
        participants[asset.recordingId] = asset.participants
    }
    const itemStart = framesToSeconds(itemFrom, fps)
    const sources = mediaItems.map((media) =>
      captionSourceOf(media, assets, fps, itemStart),
    )
    const time = framesToSeconds(frame, fps)
    if (!pin)
      return { speaker: speakerAtTime(sources, speakers, time), active: true }
    // A scene plays one recording; its items name it.
    const recordingIds = [
      ...new Set(
        sources.flatMap((s) => (s.recordingId ? [s.recordingId] : [])),
      ),
    ]
    const speaker = pinnedSpeaker(
      recordingIds.flatMap((id) => participants[id] ?? []),
      recordingIds.flatMap((id) => speakers[id] ?? []),
      pin,
    )
    return {
      speaker,
      active: activeSpeakerAt(sources, speakers, time, words) === pin,
    }
  }, [sceneId, legacyTrackId, assets, mediaItems, itemFrom, fps, frame, pin])
}
